/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewProjectForm from './AddNewProjectForm';
import AddNewProjectSuccess from './AddNewProjectSuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
  disableFields: Array,
  prefillFields: Object,
  replaceModels: Object,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewProject extends React.Component<*> {
  @observable vModelData = this.props.model;

  @observable vChildModelData = this.props.childmodel;

  /**
   * Our Default field list, of which is overridable by props
   * We use this to be
   */
  defaultFieldList: Object = {
    account: 'chooseAccount.tradingAccount',
    costCentre: 'assignCostCentre.costCentreAssign',
    project: 'assignProjects.projectAssign',
  };

  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const { parentForm, fieldList = null }: Object = this.props;

    if (fieldList) {
      this.defaultFieldList = fieldList;
    }

    const fields: Array<Object> = [
      {
        name: 'addNewProject',
        label: t('general:actions.new_project'),
        title: 'New Project',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$(`${this.defaultFieldList.account}`).extra.selectID),
            value: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
          },
          {
            name: 'projectName',
            label: t('general:projects.labels.name'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'projectCode',
            label: t('general:projects.labels.code'),
            rules: 'required|string',
            value: '',
          },
          {
            name: 'projectDescription',
            label: t('general:projects.labels.description'),
            rules: 'string',
            value: '',
          },
          {
            name: 'projectStartDate',
            label: t('general:projects.labels.start_date'),
            rules: 'required|date',
            value: '',
            related: ['addNewProject.projectEndDate'],
          },
          {
            name: 'projectEndDate',
            label: t('general:projects.labels.end_date'),
            rules: 'required|date|date_after_field:addNewProject.projectStartDate',
            value: '',
          },
          {
            name: 'chooseLead',
            label: t('general:actions.choose_project_lead'),
            rules: 'required|string',
            placeholder: 'First name, surname',
            value: null,
            extra: {
              selectID: null,
              parentId: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
            },
          },
          {
            name: 'projectEngineer',
            label: t('general:actions.choose_project_engineers'),
            rules: '',
            value: '',
            extra: {
              selectID: null,
              parentId: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
              actionButton: false,
            },
          },
        ],
      },
      {
        // PROJECT LOCATION DETAIL SECTION
        name: 'location',
        label: t('general:projects.labels.location'),
        fields: [
          {
            name: 'countryId',
            value: null,
            rules: 'string',
          },
          {
            name: 'locationSiteId',
            value: '',
            rules: 'string',
          },
          {
            name: 'locationCompany',
            label: t('general:company'),
            rules: 'required|string',
            value: null,
            extra: {
              selectID: null,
              parentId: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
              actionButton: false,
              setSelectedFields: [
                {
                  field: 'locationSite',
                  value: null,
                },
                {
                  field: 'locationBuilding',
                  value: null,
                },
                {
                  field: 'locationRoom',
                  value: null,
                },
                {
                  field: 'locationSystem',
                  value: null,
                },
              ],
            },
          },
          {
            name: 'locationSite',
            label: t('general:locations.labels.site'),
            rules: 'required|string',
            value: null,
            extra: {
              selectID: null,
              parentId: null,
              actionButton: false,
              setSelectedFields: [
                {
                  field: 'countryId',
                  value: 'country_id',
                },
                {
                  field: 'locationBuilding',
                  value: null,
                },
                {
                  field: 'locationRoom',
                  value: null,
                },
                {
                  field: 'locationSystem',
                  value: null,
                },
              ],
              childExtraFilters: [
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'locationCompany',
                  isExtra: true,
                  value: 'selectID',
                },
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'countryId',
                  isExtra: false,
                  value: 'value',
                },
                {
                  type: 'string',
                  value: '?parent_id=',
                },
              ],
            },
          },
          {
            name: 'locationBuilding',
            label: t('general:locations.labels.building_transport'),
            rules: 'string',
            value: '',
            extra: {
              selectID: null,
              parentId: null,
              actionButton: false,
              setSelectedFields: [
                {
                  field: 'locationRoom',
                  value: null,
                },
                {
                  field: 'locationSystem',
                  value: null,
                },
              ],
              childExtraFilters: [
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'locationCompany',
                  isExtra: true,
                  value: 'selectID',
                },
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'countryId',
                  isExtra: false,
                  value: 'value',
                },
                {
                  type: 'string',
                  value: '?parent_id=',
                },
              ],
            },
          },
          {
            name: 'locationRoom',
            label: t('general:locations.labels.lab_room'),
            value: '',
            rules: 'string',
            extra: {
              selectID: null,
              parentId: null,
              actionButton: false,
              setSelectedFields: [
                {
                  field: 'locationSystem',
                  value: null,
                },
              ],
              childExtraFilters: [
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'locationCompany',
                  isExtra: true,
                  value: 'selectID',
                },
                {
                  type: 'formField',
                  formName: 'location',
                  formField: 'countryId',
                  isExtra: false,
                  value: 'value',
                },
                {
                  type: 'string',
                  value: '?parent_id=',
                },
              ],
            },
          },
          {
            name: 'locationSystem',
            label: t('general:locations.labels.system_workspace'),
            value: '',
            rules: 'string',
            extra: {
              selectID: null,
              parentId: null,
              actionButton: false,
            },
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(form: Object) {
        const {
          tradingAccount,
          projectName,
          projectCode,
          projectDescription,
          projectStartDate,
          projectEndDate,
          projectEngineer,
        }: Object = form.values().addNewProject;

        const vLocationId: string =
          form.$('location.locationSystem').extra.selectID ||
          form.$('location.locationRoom').extra.selectID ||
          form.$('location.locationBuilding').extra.selectID ||
          form.$('location.locationSite').extra.selectID ||
          form.$('location.locationCompany').extra.selectID;

        const arrProjectEngineer = projectEngineer ? projectEngineer.split(',') : [];

        const jsonObj: Object = {
          account_id: tradingAccount, //parentForm.$('chooseAccount.tradingAccount').value,
          name: projectName,
          code: projectCode,
          description: projectDescription,
          start_date: projectStartDate,
          end_date: projectEndDate,
          assignee_id: form.$('addNewProject.chooseLead').extra.selectID,
          engineer_ids: arrProjectEngineer,
          location_id: vLocationId,
        };

        !projectDescription && delete jsonObj.description;

        actionDrawerStore.userAddProject(jsonObj);
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.new_project'),
      }
    );
  };

  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const {
      actionDrawerStore,
      hideSections = [],
      prefillFields = {},
      replaceModels = {},
    }: TypeProps = this.props;
    const parentForm: Object = this.props.parentForm;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={styles['action-drawer-content']}>
          <ActionDrawerHeader title={t('general:actions.create_new_project')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <AddNewProjectSuccess
          parentForm={parentForm}
          model={this.props.model}
          childmodel={this.props.childmodel}
          localState={this.props.localState}
          fields={this.defaultFieldList}
        />
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_project')} />
        <AddNewProjectForm
          form={this.createForm()}
          accountsModel={this.props.accountsModel}
          hideSections={[...hideSections]}
          prefillFields={{ ...prefillFields }}
          replaceModels={{ ...replaceModels }}
        />
      </div>
    );
  }
}
